import type { NotNil, Provider } from "@infra-blocks/types";

export type Message = string | Provider<string>;

function getMessage(
  prefix: string,
  joinedWith: string,
  message?: Message,
): string {
  if (message == null) {
    return prefix;
  }
  if (typeof message === "string") {
    return `${prefix}${joinedWith}${message}`;
  }
  return `${prefix}${joinedWith}${message()}`;
}

export class NullValueError extends Error {
  constructor(message?: Message) {
    super(getMessage("unexpected null value", ": ", message));
    this.name = NullValueError.name;
  }
}

/**
 * Checks that a value is not null or undefined, and return that one if so.
 *
 * If the value is undefined or null, this function will throw a {@link NullValueError}.
 * Otherwise, the non null value is returned to the caller, for convenient one-liners.
 *
 * Optionally, the user may provide more information to the error message. This is accomplished
 * in one of two ways:
 * - Pass a message directly as a string.
 * - Pass a message indirectly as a function returning a string (only lazily evaluated on error).
 *
 * @param value - The value to test.
 * @param message - A optional  string, or string provider, to add additional details on
 * the error.
 *
 * @returns The value, if it's not null.
 */
export function checkNotNil<T>(value: T, message?: Message): NotNil<T> {
  if (value != null) {
    return value;
  }
  throw new NullValueError(message);
}
